// TicketDetail.jsx

import { FiArrowLeft } from "react-icons/fi";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { getTicketsView, PostCommentAdd } from "../../ApiHelper/url_Helpers";
import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import AttachmentPreview from "../../Components/AttachmentPreview";
import Loader from "../../Components/Loader";
import CommonTable from "../../Components/CommonTable";
import { PulseLoader } from "react-spinners";
import { toasterError, toasterSuccess } from "../../Components/toaster";

export const TicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const ticketId = location?.state?.ticketId || id;
  const fetchedRef = useRef();
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [ticketData, setTicketData] = useState(null);
  const [comment, setComment] = useState("");

  const getTicketsApi = async () => {
    setLoading(true);
    try {
      if (ticketId) {
        const response = await getTicketsView(ticketId);
        setTicketData(response);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async (values) => {
    setIsLoading(true);
    let payload = {
      message: values,
    };
    try {
      const response = await PostCommentAdd(payload, ticketId);
      if (response.status !== 200) {
        getTicketsApi();
        setComment("");
        toasterSuccess("Comment added successfully.");
        setTimeout(()=>{
          setIsLoading(false);
        },1000)
      }else{
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      toasterError("Failed to add comment.");
      console.error("Upload Error 👉", error);
    }
  };

  useEffect(() => {
  if (!ticketId) return;
  if (fetchedRef.current) return;

  fetchedRef.current = true;
  getTicketsApi();
}, [ticketId]);

  return (
    <div className="p-4 sm:p-6 flex-1 bg-gray-50 min-h-screen max-w-full overflow-x-hidden">
      {/* Back + Title */}
      <div className="flex items-center mb-2 gap-2">
        <button
          onClick={() => navigate(-1)}
          className="text-sm flex items-center gap-1 text-gray-700 hover:text-black cursor-pointer"
        >
          <FiArrowLeft className="text-lg" />
        </button>

        <span className="text-xl font-semibold">
          Ticket Details – {ticketData?.ticket_id || " "}
        </span>
      </div>

      <p className="text-gray-500 mb-5  ml-2 sm:ml-7 text-sm ">
        Review your request and HR’s latest updates.
      </p>
      {loading ? (
        <Loader />
      ) : (
        <div>
          <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200 mb-6">
            {/* Grid Responsive */}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 sm:gap-x-10">
              <p className="flex items-center">
                <span className="w-28 text-sm text-gray-500 font-semibold">
                  Status:
                </span>
                <span className="text-orange-500 text-sm font-semibold">
                  {ticketData?.status}
                </span>
              </p>

              <p className="flex items-center">
                <span className="w-28 text-sm text-gray-500 font-semibold">
                  Created On:
                </span>
                <span className="text-sm font-semibold">
                  {dayjs(ticketData?.created_at).format("DD-MM-YYYY")}
                </span>
              </p>

              <p className="flex items-center">
                <span className="w-28 text-sm text-gray-500 font-semibold">
                  Category:
                </span>
                <span className="text-sm font-semibold">
                  {ticketData?.category_name}
                </span>
              </p>

              <p className="flex items-center">
                <span className="w-28 text-sm text-gray-500 font-semibold">
                  Last Updated:
                </span>
                <span className="text-sm font-semibold">
                  {ticketData?.updated_at}
                </span>
              </p>

              <p className="flex items-center">
                <span className="w-28 text-sm text-gray-500 font-semibold">
                  Priority:
                </span>
                <span className="text-sm font-semibold">
                  {ticketData?.priority}
                </span>
              </p>
            </div>

            {/* Description */}
            <div className="mt-4">
              <p className="mb-1 text-sm text-gray-500 font-semibold">
                Issue Description:
              </p>
              <p className="text-gray-800 text-sm font-semibold">
                {ticketData?.description}
              </p>
            </div>

            {/* Attachment */}
            <div className="mt-4">
              <p className="text-sm text-gray-500 font-semibold mb-1">
                Attachments:
              </p>
              {/* Attachment */}
              <div className="mt-3">
                <div className="grid grid-cols-3 gap-2">
                  {ticketData?.attachments?.map((file, index) => (
                    <AttachmentPreview
                      key={`${file.file_name}-${index}`}
                      file={file?.file_url}
                      onRemove={() => {}}
                      isRemove={false}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Updates Table */}
          <div className="bg-white p-0 rounded-xl shadow-sm border border-gray-200 mb-6 overflow-x-auto">
            <table className="w-full min-w-[600px] text-left">
              <thead className="bg-gray-50 text-black border-b border-gray-200">
                <tr>
                  <th className="py-3 px-4 text-sm font-medium">Date/Time</th>
                  <th className="py-3 px-4 text-sm font-medium">
                    Employee Name
                  </th>
                  <th className="py-3 px-4 text-sm font-medium">Comment</th>
                </tr>
              </thead>

              <tbody>
                {ticketData?.comments?.length  > 0 && !isLoading  ? (
                  ticketData?.comments?.map((u, i) => (
                    <tr key={i}>
                      <td className="py-3 px-4 text-sm text-gray-600 ">
                        {u.created_at}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 ">
                        {u.author_name}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 ">
                        {u.message}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="py-6">
                      <div className="flex items-center justify-center w-full">
                        {isLoading ? (
                          <PulseLoader color="#35e6e9" />
                        ) : (
                          <span className="text-sm text-gray-600 font-medium">
                            No Comments Available
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Comment Box */}
          {(ticketData?.status !== "closed") && (
            <div>
            <div className="mb-10">
              <p className="font-semibold mb-2 text-sm">Add comment</p>
              <textarea
                placeholder="Type here"
                className="w-full text-sm    border border-gray-200 rounded-lg p-3 h-24 focus:outline-none"
                value={comment}
              onChange={(e) => setComment(e.target.value)}
            ></textarea>
          </div>

          {/* Button */}
          <div className="flex justify-end">
            <button
              className="bg-[#00b8d9] text-white text-sm px-5 py-2.5 rounded-lg hover:bg-[#01a7c4] cursor-pointer"
              onClick={() => {
                handleAddComment(comment);
              }}
              disabled={comment.trim() === "" || isLoading }
            >
              Update Ticket
            </button>
          </div>
          </div>
          )}
        </div>
      )}
    </div>
  );
};
